import { context, trace } from '@opentelemetry/api';
import { defineStore } from 'pinia';
import { ref, shallowReactive } from 'vue';

import { AnalyticsMetadata } from '@/lib/personalization/dynamicYield';

export interface FeatureFlags extends Record<string, boolean | undefined> {
  adSurvey?: boolean;
  cartLayoutUpdates?: boolean;
  categoryColors?: boolean;
  disableReferral?: boolean;
  discountCodeForm?: boolean;
  enableSubscriptionOnCSB?: boolean;
  expressCheckout?: boolean;
  goatCsb?: boolean;
  miniPdp?: boolean;
  pinGreetingCards?: boolean;
  ppaDefaultToSmallestVariant?: boolean;
  pricePackPhase2?: boolean;
  showAddOn?: boolean;
  separateFilterSortPLP?: boolean;
  separateFilterSortSearch?: boolean;
  socialProofMessaging?: boolean;
  strikethroughPricing?: boolean;
  supportACH?: boolean;
}

export const layers = ['local', 'session', 'site', 'visitor'] as const;
export type Layer = (typeof layers)[number];

export type FlagsByLayer = Record<Layer, FeatureFlags>;

export interface ExternalFlagMetadata extends AnalyticsMetadata {
  source: 'Dynamic Yield';
  type: 'external';
}

export interface InternalFlagMetadata {
  experimentId: keyof FeatureFlags;
  experimentName: string;
  source: 'Webstore';
  type: 'internal';
  variationId: boolean;
  variationName: 'control' | 'experiment';
}

export type FlagExperimentMetadata = ExternalFlagMetadata | InternalFlagMetadata;

export const internalExperiments: Record<
  keyof FeatureFlags,
  Omit<InternalFlagMetadata, 'variationId' | 'variationName'>
> = {
  enableSubscriptionOnCSB: {
    experimentId: 'enableSubscriptionOnCSB',
    experimentName: 'CSB Subscription',
    source: 'Webstore',
    type: 'internal',
  },
};

export const useFeatureFlags = defineStore('featureFlags', () => {
  const flagsByLayer = ref<FlagsByLayer>({
    local: {},
    session: {},
    site: {},
    visitor: {},
  });

  const flags = shallowReactive(
    new Proxy<FeatureFlags>(
      {},
      {
        get: (target, property) => {
          const flagName = property.toString();
          const flagValue = target[flagName];
          if (flagName.substring(0, 2) !== '__' && flagName.substring(0, 7) !== 'Symbol(') {
            trace.getSpan(context.active())?.addEvent('feature_flag', {
              'feature_flag.key': flagName,
              'feature_flag.provider_name': 'nuts.com',
              'feature_flag.variant': (flagValue ?? 'null').toString(),
            });
          }
          return flagValue;
        },
      },
    ),
  );

  interface FlagsMetadataHash {
    [key: string]: FlagExperimentMetadata;
  }
  const flagsMetadata = ref<Partial<FlagsMetadataHash>>({});

  return {
    flags,
    flagsByLayer,
    flagsMetadata,
  };
});
